import Vue from 'vue'
import Vuex from 'vuex'
import AppUser from '../classes-shared/user/AppUser'
import RouterHelper from '../classes-shared/routerHelper/RouterHelper'
import router from '../router.js'
import Basket from '../classes-shared/basket/Basket'
import NavigationStores from '../classes-shared/navigationStores/NavigationStores'
import OverviewPricing from '@/store/modules/OverviewPricing'
import DetailsPricing from '@/store/modules/DetailsPricing'
import UserProductDefinitionBookmarks from '@/store/modules/UserProductDefinitionBookmarks'
import Products from '../classes-shared/products/Products'
import PriceOverrideViewForEvents from '@/store/modules/PriceOverrideViewForEvents'
import { shopInstance } from '../classes-shared/utils/axiosInstance'
import Cookies from 'js-cookie'
import Authentication from '../classes-shared/authentication/Authentication'
import Seasons from '@/classes-shared/season/Seasons'
import Season from '@/classes-shared/season/Season'

Vue.use(Vuex)

export default new Vuex.Store({
  modules: {
    OverviewPricing,
    DetailsPricing,
    UserProductDefinitionBookmarks,
    PriceOverrideViewForEvents,
  },
  getters: {
    scrollHintAlreadyDisplayed: (state) => () => {
      return state.scrollHint
    },

    getBasketInstance: (state) => () => {
      return state.basketInstance
    },

    getProducts: (state) => () => {
      return state.products
    },

    getSelectedSeason: (state) => () => {
      return state.selectedSeason
    },

    isActivePricingModel: (state) => (pricingModel) => {
      return state.products.isActivePricingModel(pricingModel)
    },

    isOnlyStaticallyPriced: (state) => () => {
      if (state.products) {
        return state.products.isOnlyStaticallyPriced()
      }

      return false
    },

    getAppDestinationInstance: (state) => () => {
      return state.appDestinationInstance
    },

    getCurrentDestinationInstance: (state) => () => {
      return state.currentDestinationInstance
    },

    getDestinationInstanceSlug: (state) => (destinationInstance) => {
      let destSlug = state.currentDestinationInstance?.slug
      if (destinationInstance) {
        destSlug = destinationInstance.getSlug()
      }
      return destSlug
    },

    getDestinationInstanceBaseUrl: (state) => (destinationInstance) => {
      let baseUrl = state.currentDestinationInstance?.getPeApi()
      if (destinationInstance) {
        baseUrl = state.currentDestinationInstance.getPeApi(
          destinationInstance.getSlug()
        )
      }
      return baseUrl
    },

    // get all available destinations, where the app user has permission for
    getAvailableDestinationsWithPermission: (state) => (permission) => {
      return state.appUserInstance
        .getUserDestinationsInstance()
        .getDestinationsWithPermissions(permission)
    },

    getRouterHelperInstance: (state) => () => {
      return state.routerHelperInstance
    },
    getPagesInstance: (state) => () => {
      if (state.pagesInstance) return state.pagesInstance
      else return null
    },

    // get available language objects
    getLanguages: (state) => () => {
      return state.languages
    },

    // return actual language as text or as value
    getActualLanguage: (state, getters) => (asText = true) => {
      if (asText) {
        // as text
        return getters.getLanguageText(state.actualLanguage)
      } else {
        // as language key
        return state.actualLanguage
      }
    },

    getActualI18n: (state) => () => {
      let languageObject = state.languages.find((language) => {
        return language.value === state.actualLanguage
      })

      return languageObject.i18n
    },

    getLanguageLocale: (state) => () => {
      return state.languages.find((tmpLanguage) => {
        return tmpLanguage.value === state.actualLanguage
      }).locale
    },

    getLanguageText: (state) => (languageKey) => {
      let languageObject = state.languages.find((element) => {
        return element.value === languageKey
      })

      return languageObject.text
    },

    getLanguageValue: (state) => (i18n) => {
      return state.languages.find((language) => {
        return language.i18n === i18n
      }).value
    },

    getExtTransLoaded: (state) => (i18n) => {
      return state.extTransLoaded
    },

    // check whether the user is logged in or not
    isUserLoggedIn: (state) => () => {
      return state.authorization !== 0
    },

    getAppUserInstance: (state) => () => {
      return state.appUserInstance
    },

    getNavigationStores: (state) => () => {
      return state.navigationStores
    },

    // helper function to round money to 05 CHF Rp.
    // deprecated => moved to global filter in main js
    roundCHF: () => (amount) => {
      return (Math.ceil(amount * 2) / 2).toFixed(2)
    },

    /** E-Mail **/
    getMailRules: (state) => () => {
      return state.mailRules
    },

    /**
     * Wrappers
     */

    // transform birthdate from YY-mm-dd
    transformBirthdate: () => (birthdate) => {
      let dateObject = new Date(
        birthdate.slice(0, 4),
        birthdate.slice(5, 7),
        birthdate.slice(8, 10),
        0,
        0,
        0,
        0
      )
      return dateObject.toLocaleDateString()
    },
  },
  state: {
    // show scroll hint on shop
    scrollHint: false,
    /**
     * all about the destination like color etc.
     * The AppDestination can be the default destination
     */
    appDestinationInstance: null,

    // selected destination instance => is needed, so that the app user can manage multiple destinations.
    currentDestinationInstance: null,
    /**
     * the users basket with all entries etc.
     */
    basketInstance: new Basket(),

    /**
     * Instance of Products Class: e.g. pre-select products in app header.
     */
    products: null,

    selectedSeason: null,

    routerHelperInstance: new RouterHelper(router),

    appUserInstance: null,

    // for menu, footer and checkout
    pagesInstance: null,

    // save information while the user navigates, to re-set the last data when he returns from another rout
    navigationStores: new NavigationStores(),

    // indicates if logged in or out (0 = logged out)
    authorization: 0,

    actualLanguage: 'de_CH',
    languages: [
      {
        value: 'de_CH',
        locale: 'de-CH',
        text: 'DE',
        i18n: 'de',
      },
      {
        value: 'en_US',
        locale: 'en-US',
        text: 'EN',
        i18n: 'en',
      },
      {
        value: 'fr_CH',
        locale: 'fr-CH',
        text: 'FR',
        i18n: 'fr',
      },
    ],
    extTransLoaded: false,

    mailRules: [
      (v) => !!v || i18n.t('registration.isNotEmail'),
      (v) => /.+@{1}.+\.+/.test(v) || i18n.t('registration.eMailRequired'),
    ],
  },
  mutations: {
    setScrollHintAlreadyDisplayed(state) {
      state.scrollHint = true
    },

    setAppDestinationInstance(state, appDestinationInstance) {
      state.appDestinationInstance = appDestinationInstance
    },

    setCurrentDestinationInstance(state, destinationInstance) {
      state.currentDestinationInstance = destinationInstance
    },

    setBasketInstance(state, basketInstance) {
      state.basketInstance = basketInstance
    },

    setPagesInstance(state, pagesInstance) {
      state.pagesInstance = pagesInstance
    },

    setLanguage(state, languageKey) {
      // set actual language
      state.actualLanguage = languageKey

      /* global i18n */
      i18n.locale = this.getters.getActualI18n()

      // set actual user language as user language, if user is logged in
      if (state.appUserInstance) {
        state.appUserInstance.setLanguage(languageKey)
      }
    },

    setExtTransLoaded(state) {
      state.extTransLoaded = true
    },

    resetBasket(state) {
      state.basketInstance = new Basket()
    },

    setProducts(state, products = new Products()) {
      state.products = products
    },

    setSelectedSeason(state, season = new Season()) {
      state.selectedSeason = season
    },

    setUser(state, user) {
      // new app user instance
      state.appUserInstance = new AppUser(
        user.profile,
        user.regions,
        user.SkiCards
      )

      // set Language of user
      state.actualLanguage = state.appUserInstance.getLanguage()

      /* global i18n */
      i18n.locale = this.getters.getActualI18n()

      return Promise
    },

    storeAuthorization(state, authorization) {
      state.authorization = authorization
    },
  },

  actions: {
    async getMainUserFromAPI({ commit }) {
      /* global EventBus axios */
      try {
        const { data } = await shopInstance(false).get('/user')
        await commit('setUser', data)
      } catch (e) {
        EventBus.$emit('notify')
      } finally {
        return Promise
      }
    },

    // logging out app user
    async logout(
      { commit, state },
      payload = { redirectAfterLogout: 'login', notify: true }
    ) {
      EventBus.$emit('spinnerShow')
      // mark user as logged out
      state.authorization = 0
      // delete app user instance
      state.appUserInstance = null
      // delete auth token for axios requests
      const domain = Authentication.getDomain()
      const subdomain = Authentication.getSubDomain()
      Cookies.remove('authorization', { domain })
      Cookies.remove('uid', { domain })
      Cookies.remove('user', { domain })
      Cookies.remove('authtoken', { domain })
      // fix: T1-2655
      Cookies.remove('authorization', { domain: subdomain })
      Cookies.remove('uid', { domain: subdomain })
      Cookies.remove('user', { domain: subdomain })
      Cookies.remove('authtoken', { domain: subdomain })
      // kill the products because we run into problems if a
      // user logs out and after that a new user logs in
      // without reloading the page -> then we have mixed destination products
      commit('setProducts', null)
      await router.push({ name: payload.redirectAfterLogout })
      // notify user
      if (payload.notify) {
        EventBus.$emit('notify', i18n.t('notify.logoutSuccess'), 'success')
      }
      // hide spinner
      EventBus.$emit('spinnerHide')
      return true
    },
  },
})
